"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import { getPublicMcIndividualSchedules } from "../lib/api/mc-individual-schedules";
import type { McIndividualSchedulePublic } from "../types/mc-schedules";
import { isScheduleStartAfterToday } from "../lib/isScheduleStartAfterToday";
import { formatThaiDateDdMmYy, formatThaiMonthYearDdMmYy } from "@/lib/format-thai-date";
import { getLocaleField } from "@/lib/locale-field";

interface Course {
  id: string;
  title: string;
  titleEn?: string;
  location: string;
  locationEn?: string;
  locationCode?: string;
  locationCodeEn?: string;
  price: number;
  image: string;
  date: string;
  category: string;
  categoryEn?: string;
  categoryId: string;
  description?: string;
  thaiTitleEn?: string;
  descriptionEn?: string;
  thaiTitle?: string;
  duration?: string;
  capacity?: number;
  enrolled?: number;
  availableSeats?: number;
  gradientFrom?: string;
  gradientTo?: string;
  isForKids?: boolean;
  startDateIso: string;
  endDateIso: string;
  hsrcCenterId?: string;
}

function transformScheduleToCourse(schedule: McIndividualSchedulePublic): Course {
  const raw = schedule as McIndividualSchedulePublic & { is_for_kids?: boolean };
  const isForKids =
    schedule.isForKids === true ||
    raw.is_for_kids === true;

  const start =
    schedule.startDate instanceof Date
      ? schedule.startDate
      : new Date(schedule.startDate as unknown as string);

  return {
    id: schedule.id,
    title: schedule.title,
    titleEn: (schedule as any).titleEn || "",
    location: schedule.location,
    locationEn: (schedule as McIndividualSchedulePublic & { locationEn?: string }).locationEn || schedule.location,
    locationCode: schedule.locationCode,
    locationCodeEn: schedule.locationCodeEn,
    price: schedule.price,
    image: schedule.image,
    date: schedule.date,
    startDateIso: Number.isNaN(start.getTime()) ? "" : start.toISOString(),
    endDateIso: schedule.endDate ? (schedule.endDate instanceof Date ? schedule.endDate.toISOString() : new Date(schedule.endDate as unknown as string).toISOString()) : "",
    category: schedule.subCategory || schedule.category || "beginner",
    categoryEn: (schedule as any).subCategoryEn || (schedule as any).categoryEn || "",
    categoryId: schedule.categoryId,
    description: schedule.description,
    thaiTitleEn: schedule.thaiTitleEn || '',
    descriptionEn: schedule.descriptionEn || '',
    thaiTitle: schedule.thaiTitle,
    duration: schedule.duration,
    capacity: schedule.maxSeats,
    enrolled: schedule.enrolled,
    availableSeats: schedule.availableSeats,
    gradientFrom: schedule.gradientFrom,
    gradientTo: schedule.gradientTo,
    isForKids,
    hsrcCenterId: (schedule as McIndividualSchedulePublic & { hsrcCenterId?: string }).hsrcCenterId,
  };
}

export default function CourseSection() {
  const t = useTranslations("course");
  const locale = useLocale();
  const [schedules, setSchedules] = useState<McIndividualSchedulePublic[]>([]);
  const [centers, setCenters] = useState<{id: string; name: string; nameEn?: string; code: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isStartMonthOpen, setIsStartMonthOpen] = useState(false);
  const [isEndMonthOpen, setIsEndMonthOpen] = useState(false);
  const startMonthRef = useRef<HTMLDivElement>(null);
  const endMonthRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const locationOptions = useMemo(() => [
    { value: "all", label: t("allLocations") },
    ...centers.map(center => ({ value: center.id, label: locale === 'en' && center.nameEn ? center.nameEn : center.name })),
  ], [centers, t]);

  const categoryOptions = useMemo(() => {
    const uniqueCategories = new Map<string, { name: string; nameEn?: string }>();
    schedules.forEach(schedule => {
      if (schedule.categoryId && schedule.category) {
        uniqueCategories.set(schedule.categoryId, { name: schedule.category, nameEn: (schedule as any).categoryEn });
      }
    });
    
    const options = [{ value: "all", label: t("allCategories") }];
    Array.from(uniqueCategories.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name)).forEach(([id, cat]) => {
      options.push({ value: id, label: locale === 'en' && cat.nameEn ? cat.nameEn : cat.name });
    });
    
    return options;
  }, [schedules, t, locale]);
  
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    fetch('/api/centers/public', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.data)) {
          setCenters(data.data);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    async function fetchSchedules() {
      setLoading(true);
      setError(null);
      
      try {
        const result = await getPublicMcIndividualSchedules();
        
        if (result.error) {
          setError(result.error.message);
          setSchedules([]);
        } else {
          setSchedules(result.data || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setSchedules([]);
      }
      
      setLoading(false);
    }
    
    fetchSchedules();
  }, []);
  
  const formatDateDisplay = (dateString: string) => formatThaiMonthYearDdMmYy(dateString, locale);

  const formatDateRangeDisplay = () => {
    if (!startDate && !endDate) return "";
    if (startDate && !endDate) return formatDateDisplay(startDate);
    if (!startDate && endDate) return formatDateDisplay(endDate);
    return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
  };

  const handleCalendarIconClick = () => {
    setIsDateRangeOpen(!isDateRangeOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDateRangeOpen && !target.closest('.date-range-container')) {
        setIsDateRangeOpen(false);
      }
      if (isLocationDropdownOpen && locationDropdownRef.current && !locationDropdownRef.current.contains(target)) {
        setIsLocationDropdownOpen(false);
      }
      if (isCategoryDropdownOpen && categoryDropdownRef.current && !categoryDropdownRef.current.contains(target)) {
        setIsCategoryDropdownOpen(false);
      }
      if (isStartMonthOpen && startMonthRef.current && !startMonthRef.current.contains(target)) {
        setIsStartMonthOpen(false);
      }
      if (isEndMonthOpen && endMonthRef.current && !endMonthRef.current.contains(target)) {
        setIsEndMonthOpen(false);
      }
    };

    if (isDateRangeOpen || isLocationDropdownOpen || isCategoryDropdownOpen || isStartMonthOpen || isEndMonthOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDateRangeOpen, isLocationDropdownOpen, isCategoryDropdownOpen, isStartMonthOpen, isEndMonthOpen]);

  const courses = schedules.map(transformScheduleToCourse);

  const dateRange = useMemo(() => {
    if (schedules.length === 0) return { months: [] as { value: string; label: string }[] };
    
    const uniqueMonths = new Map<string, string>();
    schedules.forEach(schedule => {
      const date = new Date(schedule.startDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const value = `${year}-${String(month).padStart(2, "0")}`;
      const label = formatThaiMonthYearDdMmYy(value, locale);
      uniqueMonths.set(value, label);
    });
    
    return {
      months: Array.from(uniqueMonths.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, label]) => ({ value, label }))
    };
  }, [schedules]);

  const filteredCourses = courses.filter((course) => {
    if (!course.startDateIso || !isScheduleStartAfterToday(course.startDateIso)) {
      return false;
    }

    if (selectedLocation !== "all" && course.hsrcCenterId !== selectedLocation) {
      return false;
    }

    if (selectedCategory !== "all" && course.categoryId !== selectedCategory) {
      return false;
    }

    if (startDate || endDate) {
      const courseDateParts = course.date.split('/');
      if (courseDateParts.length === 3) {
        const courseDay = parseInt(courseDateParts[0]);
        const courseMonth = parseInt(courseDateParts[1]) - 1;
        const buddhistYear = parseInt(courseDateParts[2]);
        const gregorianYear = buddhistYear < 100 ? buddhistYear + 2500 - 543 : buddhistYear - 543;
        const courseDateObj = new Date(gregorianYear, courseMonth, courseDay);
        
        if (startDate) {
          const [startYear, startMonth] = startDate.split("-");
          const start = new Date(parseInt(startYear), parseInt(startMonth) - 1, 1);
          if (courseDateObj < start) return false;
        }
        if (endDate) {
          const [endYear, endMonth] = endDate.split("-");
          const end = new Date(parseInt(endYear), parseInt(endMonth), 0);
          if (courseDateObj > end) return false;
        }
      }
    }

    return true;
  });

  const ROWS = 2;

  const getColumnsPerView = useCallback((): number => {
    if (typeof window === "undefined") return 4;
    const vw = window.innerWidth;
    if (vw >= 1728) return 4;
    if (vw >= 1024) return 3;
    if (vw >= 640) return 2;
    return 1;
  }, []);

  const [columnsPerView, setColumnsPerView] = useState(getColumnsPerView);

  useEffect(() => {
    const handleResize = () => setColumnsPerView(getColumnsPerView());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getColumnsPerView]);

  const columns = useMemo(() => {
    const result: Course[][] = [];
    for (let i = 0; i < filteredCourses.length; i += ROWS) {
      result.push(filteredCourses.slice(i, i + ROWS));
    }
    return result;
  }, [filteredCourses]);

  const totalColumns = columns.length;
  const showArrows = filteredCourses.length > columnsPerView * ROWS;
  const maxScrollPos = totalColumns - columnsPerView;
  const totalPages = maxScrollPos + 1;

  const GAP = 24;

  const getActiveSlideFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const gap = width >= 1024 ? 24 : 16;
    const colW = (width - gap * (columnsPerView - 1)) / columnsPerView;
    const slide = Math.round(el.scrollLeft / (colW + gap));
    setActiveSlide(slide);
  }, [columnsPerView]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      getActiveSlideFromScroll();
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [getActiveSlideFromScroll]);

  const scrollToSlide = useCallback((index: number) => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const gap = el.clientWidth >= 1024 ? 24 : 16;
    const colW = (el.clientWidth - gap * (columnsPerView - 1)) / columnsPerView;
    el.scrollTo({ left: index * (colW + gap), behavior: "smooth" });
    setActiveSlide(index);
  }, [columnsPerView]);

  const scroll = useCallback((direction: "left" | "right") => {
    const next = direction === "left"
      ? Math.max(0, activeSlide - 1)
      : Math.min(maxScrollPos, activeSlide + 1);
    scrollToSlide(next);
  }, [activeSlide, maxScrollPos, scrollToSlide]);

  if (loading) {
    return (
      <section className="pt-0 pb-16 bg-white relative">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honda-red"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="pt-0 pb-16 bg-white relative">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
            <div className="text-center py-20 text-gray-600">
              <p>{t("loadError")}</p>
              {typeof error === 'string' && <p className="text-sm mt-2">{error}</p>}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-0 pb-16 bg-white relative">
      <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
        <div className="flex flex-row items-center justify-between mb-8 relative">
          <h2 className="text-5xl md:text-[56px] font-bold text-center text-dark mb-0 flex-1">{t("title")}</h2>
          <a href="/courses" className="text-base text-dark hover:text-honda-red transition-colors inline-flex items-center gap-2 absolute right-0" style={{ display: 'none' }}>
            <span className="text-sm">All Course</span>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="relative" ref={locationDropdownRef}>
            <button
              type="button"
              onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
              className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red text-left flex items-center justify-between relative overflow-hidden"
            >
              <span className="relative z-10">
                {locationOptions.find(opt => opt.value === selectedLocation)?.label || t('allLocations')}
              </span>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <div className="w-[50px] h-[50px] bg-honda-red flex items-center justify-center rounded">
                  <Image
                    src="/images/icons/icon-next.svg"
                    alt="Dropdown icon"
                    width={24}
                    height={24}
                    className={`w-6 h-6 object-contain transition-transform duration-300 ease-in-out ${isLocationDropdownOpen ? 'rotate-180' : ''}`}
                    style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }}
                    unoptimized
                  />
                </div>
              </div>
            </button>
            
            <div
              className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-300 ease-in-out ${
                isLocationDropdownOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-2 pointer-events-none'
              }`}
            >
              {locationOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSelectedLocation(option.value);
                    setIsLocationDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left text-base transition-colors duration-200 ${
                    selectedLocation === option.value
                      ? 'bg-honda-red text-white'
                      : 'text-dark hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="relative" ref={categoryDropdownRef}>
            <button
              type="button"
              onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
              className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red text-left flex items-center justify-between relative overflow-hidden"
            >
              <span className="relative z-10">
                {categoryOptions.find(opt => opt.value === selectedCategory)?.label || t('allCategories')}
              </span>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <div className="w-[50px] h-[50px] bg-honda-red flex items-center justify-center rounded">
                  <Image
                    src="/images/icons/icon-next.svg"
                    alt="Dropdown icon"
                    width={24}
                    height={24}
                    className={`w-6 h-6 object-contain transition-transform duration-300 ease-in-out ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                    style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }}
                    unoptimized
                  />
                </div>
              </div>
            </button>
            
            <div
              className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-300 ease-in-out ${
                isCategoryDropdownOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-2 pointer-events-none'
              }`}
            >
              {categoryOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(option.value);
                    setIsCategoryDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left text-base transition-colors duration-200 ${
                    selectedCategory === option.value
                      ? 'bg-honda-red text-white'
                      : 'text-dark hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="relative date-range-container">
            <div 
              className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors flex items-center date-input-english"
              onClick={handleCalendarIconClick}
            >
              <span className="text-base text-dark">
                {formatDateRangeDisplay() || t('dateRangePlaceholder')}
              </span>
            </div>
            
            {isDateRangeOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 date-range-container">
                <div className="grid grid-cols-2 gap-4">
                  <div ref={startMonthRef} className="relative">
                    <label className="block text-sm text-gray-600 mb-2">{t('startMonth')}</label>
                    <button
                      type="button"
                      onClick={() => setIsStartMonthOpen(!isStartMonthOpen)}
                      className="w-full h-[40px] px-3 pr-8 rounded border border-gray-300 text-base text-dark bg-white text-left hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red"
                    >
                      {startDate ? dateRange.months.find(m => m.value === startDate)?.label || t('select') : t('select')}
                    </button>
                    {isStartMonthOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                        {dateRange.months.map((month) => (
                          <button
                            key={month.value}
                            type="button"
                            onClick={() => {
                              setStartDate(month.value);
                              if (!endDate || endDate < month.value) {
                                setEndDate(month.value);
                              }
                              setIsStartMonthOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                              startDate === month.value ? 'bg-honda-red text-white hover:bg-honda-red' : ''
                            }`}
                          >
                            {month.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div ref={endMonthRef} className="relative">
                    <label className="block text-sm text-gray-600 mb-2">{t('endMonth')}</label>
                    <button
                      type="button"
                      onClick={() => setIsEndMonthOpen(!isEndMonthOpen)}
                      className="w-full h-[40px] px-3 pr-8 rounded border border-gray-300 text-base text-dark bg-white text-left hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red"
                    >
                      {endDate ? dateRange.months.find(m => m.value === endDate)?.label || t('select') : t('select')}
                    </button>
                    {isEndMonthOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                        {dateRange.months
                          .filter(m => !startDate || m.value >= startDate)
                          .map((month) => (
                          <button
                            key={month.value}
                            type="button"
                            onClick={() => {
                              setEndDate(month.value);
                              setIsEndMonthOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                              endDate === month.value ? 'bg-honda-red text-white hover:bg-honda-red' : ''
                            }`}
                          >
                            {month.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setIsDateRangeOpen(false);
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-dark transition-colors"
                  >
                    {t('clear')}
                  </button>
                  <button
                    onClick={() => setIsDateRangeOpen(false)}
                    className="px-4 py-2 text-sm bg-honda-red text-white rounded hover:bg-honda-red-dark transition-colors"
                  >
                    {t('apply')}
                  </button>
                </div>
              </div>
            )}
            
            <div 
              onClick={handleCalendarIconClick}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-[50px] h-[50px] rounded bg-honda-red flex items-center justify-center cursor-pointer hover:bg-honda-red-dark transition-colors z-10"
            >
              <Image
                src="/images/icons/ICON-Calendar.svg"
                alt="Calendar icon"
                width={32}
                height={32}
                className="w-8 h-8 object-contain filter brightness-0 invert"
              />
            </div>
          </div>
        </div>

        <div className="relative w-full">
          {showArrows && (
            <button
              type="button"
              onClick={() => scroll("left")}
              disabled={activeSlide === 0}
              className={`absolute left-0 top-1/2 z-10 hidden h-10 w-10 -translate-x-12 -translate-y-1/2 items-center justify-center rounded-lg transition-opacity lg:flex ${
                activeSlide === 0
                  ? 'opacity-30 cursor-not-allowed text-dark'
                  : 'text-dark hover:opacity-70'
              }`}
              aria-label="Previous"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex items-start w-full gap-4 lg:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            onScroll={getActiveSlideFromScroll}
            onTouchEnd={() => {
              let ticks = 0;
              const maxTicks = 20;
              const poll = () => {
                getActiveSlideFromScroll();
                ticks++;
                if (ticks < maxTicks) {
                  requestAnimationFrame(poll);
                }
              };
              requestAnimationFrame(poll);
            }}
          >
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-16px)] 3xl:w-[calc(25%-18px)] flex flex-col gap-4 lg:gap-6 snap-start">
                {column.map((course) => {
                    const detailHref =
                      course.isForKids === true
                        ? `/course/detail/forkid?id=${course.id}`
                        : `/course/detail?id=${course.id}`;
                    const locTitle = getLocaleField(course, locale, "title");
                    const locThaiTitle = getLocaleField(course, locale, "thaiTitle");
                    return (
                      <Link key={course.id} href={detailHref} className="block bg-transparent rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group h-full flex flex-col">
                        <div className="relative aspect-[2/1] bg-gray-200 overflow-hidden">
                          <Image
                            src={course.image}
                            alt={locTitle}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                            unoptimized={course.image.startsWith('/uploads/')}
                          />
                          {course.isForKids === true && (
                            <div className="absolute top-0 left-[5%] z-10 pointer-events-none">
                              <Image
                                src="/images/icons/kids.svg"
                                alt={t("courseForKids")}
                                width={72}
                                height={42}
                                className="w-[clamp(25px,5vw,50px)] h-auto"
                                unoptimized
                              />
                            </div>
                          )}
                          <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded flex items-center gap-2">
                            <Image
                              src="/images/icons/icon-center-white.svg"
                              alt="Location icon"
                              width={12}
                              height={12}
                              className="w-3 h-3 flex-shrink-0 object-contain"
                            />
                            <span className="text-white text-xs font-medium">{locale === 'en' && course.locationCodeEn ? course.locationCodeEn : course.locationCode || course.location}</span>
                          </div>
                        </div>

                        <div 
                          className="pt-5 px-5 pb-5 text-white min-h-[160px] sm:min-h-[180px] lg:min-h-[200px] flex flex-col flex-1"
                          style={{
                            background: `linear-gradient(to bottom right, ${course.gradientFrom || '#E50021'}, ${course.gradientTo || '#730011'})`
                          }}
                        >
                          <h3 className="text-sm font-semibold mb-2">{locTitle}</h3>
                          
                          {locThaiTitle && (
                            <h4 className="text-lg font-semibold mb-2 line-clamp-2 min-h-[48px] leading-7">
                              {locThaiTitle}
                            </h4>
                          )}
                          
                          {getLocaleField(course, locale, "description") && (
                            <p className="text-sm mb-4 line-clamp-2 opacity-90 text-[11px] sm:text-xs lg:text-sm border-t border-b border-white/20 py-3">{getLocaleField(course, locale, "description")}</p>
                          )}

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="flex items-start gap-2 text-[11px] sm:text-xs lg:text-sm">
                              <Image
                                src="/images/icons/ICON-Calendar.svg"
                                alt="Calendar icon"
                                width={16}
                                height={16}
                                className="w-4 h-4 flex-shrink-0 mt-0.5 object-contain"
                                unoptimized
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="break-words 2xl:whitespace-nowrap 2xl:text-xs">{course.startDateIso ? formatThaiDateDdMmYy(course.startDateIso, locale) : course.date}{course.endDateIso ? ` - ${formatThaiDateDdMmYy(course.endDateIso, locale)}` : ''}</span>
                                {course.duration && (
                                  <span className="text-sm sm:text-lg lg:text-lg font-semibold">{String(course.duration).replace(/[^0-9]/g, '') || course.duration} {t('dayUnit')}</span>
                                )}
                              </div>
                            </div>

                            {(course.capacity || course.availableSeats !== undefined) && (
                              <div className="flex items-start gap-2 text-[11px] sm:text-xs lg:text-sm 2xl:text-xs border-l border-white/30 pl-4">
                                <Image
                                  src="/images/icons/icon-helmet-white.svg"
                                  alt="Helmet icon"
                                  width={16}
                                  height={16}
                                  className="w-4 h-4 flex-shrink-0 mt-0.5 object-contain"
                                  unoptimized
                                />
                                <div className="flex flex-col min-w-0">
                                  <span>{t('bookingLabel')}</span>
                                  <span className="break-words text-sm sm:text-lg lg:text-lg font-semibold">
                                    {course.capacity !== undefined && course.availableSeats !== undefined 
                                      ? t("capacityValueEnrolled", {
                                          enrolled: course.enrolled !== undefined
                                            ? course.enrolled
                                            : course.capacity - course.availableSeats,
                                          capacity: course.capacity,
                                        })
                                      : course.capacity 
                                      ? t("capacityValueCount", { count: course.capacity })
                                      : ''}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="border-t border-white/30 mb-4"></div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Image
                                src="/images/icons/icon-baht-white.svg"
                                alt="Baht icon"
                                width={24}
                                height={24}
                                className="w-6 h-6 object-contain"
                                unoptimized
                              />
                              <span className="text-2xl font-medium">{course.price.toLocaleString()}</span>
                            </div>
                            
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center transition-colors">
                              <Image
                                src="/images/icons/icon-next.svg"
                                alt="Arrow icon"
                                width={20}
                                height={20}
                                className="w-5 h-5 object-contain -rotate-90"
                                style={{ filter: 'brightness(0) saturate(100%) invert(11%) sepia(100%) saturate(7471%) hue-rotate(353deg) brightness(90%) contrast(112%)' }}
                                unoptimized
                              />
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-20 text-gray-600">
              <p>{t("noCoursesFound")}</p>
            </div>
          )}

          {showArrows && (
            <button
              type="button"
              onClick={() => scroll("right")}
              disabled={activeSlide >= maxScrollPos}
              className={`absolute right-0 top-1/2 z-10 hidden h-10 w-10 translate-x-12 -translate-y-1/2 items-center justify-center rounded-lg transition-opacity lg:flex ${
                activeSlide >= maxScrollPos
                  ? 'opacity-30 cursor-not-allowed text-dark'
                  : 'text-dark hover:opacity-70'
              }`}
              aria-label="Next"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {showArrows && totalPages > 1 && (
            <div className="relative flex items-center justify-center mt-4">
              <button
                type="button"
                onClick={() => scroll("left")}
                disabled={activeSlide === 0}
                className={`absolute left-0 flex items-center justify-center transition-opacity text-dark lg:hidden ${
                  activeSlide === 0 ? "opacity-30 cursor-not-allowed" : "hover:opacity-70"
                }`}
                aria-label="Previous"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i).map((dotIdx) => (
                  <button
                    key={dotIdx}
                    type="button"
                    onClick={() => scrollToSlide(dotIdx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      activeSlide === dotIdx
                        ? "bg-honda-red"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                    aria-label={"Go to position " + (dotIdx + 1)}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => scroll("right")}
                disabled={activeSlide >= maxScrollPos}
                className={`absolute right-0 flex items-center justify-center transition-opacity text-dark lg:hidden ${
                  activeSlide >= maxScrollPos ? "opacity-30 cursor-not-allowed" : "hover:opacity-70"
                }`}
                aria-label="Next"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
